import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Header, Footer } from '../Common';
import HeroBannerV2 from '@ajat/design-system/ContentBlocks/HeroBannerV2';
import Fullwidth from '@ajat/design-system/ContentBlocks/Fullwidth';
import ProductsGrid from '@ajat/design-system/ContentBlocks/ProductsGrid';
import USP from '@ajat/design-system/ContentBlocks/USP';
import ContentBanner from '@ajat/design-system/ContentBlocks/ContentBanner';
import useWidget from '../../../../hooks/useWidget';
import useShopCardPrices from '../../../../hooks/useShopCardPrices';
import { applyShopPrices } from '../../../../services/shopCardPrices';
import useBrandSettings from '../../../../hooks/useBrandSettings';
import useMenuItemListing from '../../../../hooks/useMenuItemListing';
import useMenuItemNavigation from '../../../../hooks/useMenuItemNavigation';
import { mapProductCarouselItems, mapMenuItemListingCards } from '../../../../hooks/widgetMappers';
import { pushNotification } from '@ajat/design-system/Notification/useNotification';
import { BOOKED_PARAM, BOOKED_VALUE } from '../../../../services/meetingApi';
import heroDesktop from '../../../../assets/cls-dk/landing/Hero-Banner-Desktop.jpg';
import heroMobile from '../../../../assets/cls-dk/landing/Hero-Banner-Mobile.jpg';
import '../../Common/Landing/Landing.css';

/* ══════════════════════════════════════════════════════════════════════════
 * CLS-DK Landing Page
 * Responsive: mobile-first, desktop ≥ 1024px
 * ══════════════════════════════════════════════════════════════════════════ */
const Landing = () => {
  const navigate = useNavigate();

  /* Set global brand on mount, restore previous on unmount */
  useEffect(() => {
    const prev = document.documentElement.getAttribute('data-brand');
    document.documentElement.setAttribute('data-brand', 'CLS');
    return () => {
      if (prev) document.documentElement.setAttribute('data-brand', prev);
      else document.documentElement.removeAttribute('data-brand');
    };
  }, []);

  /* ── "Meeting booked" confirmation ──
   * StudentMeet returns the customer here with ?meeting=booked after they pick
   * a slot (see services/meetingApi.js). Show the toast once, then drop the
   * param — query string ONLY, never the path: on the public domains the path
   * shown is a translated one (config/translatingHistory). */
  const bookedToastShown = useRef(false);
  useEffect(() => {
    if (bookedToastShown.current) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get(BOOKED_PARAM) !== BOOKED_VALUE) return;
    bookedToastShown.current = true;

    pushNotification({
      type: 'success',
      title: 'Dit møde afventer godkendelse i Meetbook.',
      message: 'Du burde modtage en e-mail snart.',
      duration: 8000,
    });

    try {
      const url = new URL(window.location.href);
      url.searchParams.delete(BOOKED_PARAM);
      const search = url.searchParams.toString();
      window.history.replaceState(
        window.history.state,
        '',
        `${url.pathname}${search ? `?${search}` : ''}${url.hash}`,
      );
    } catch { /* non-browser context — ignore */ }
  }, []);

  /* ── Fetch CMS widgets ── */
  const { data: collectionWidget } = useWidget('clsdk', 'homepage-products');
  const { data: packagesWidget }   = useWidget('clsdk', 'homepage-packages');

  /* Demo cards (shown until the collection / CMS cards load) carry PROD product ids /
   * package slugs in their click target so the jump into the school shop matches by id.
  /* ── Product sections = the menu items' default collections (showcase cards,
   * served even with the open shop disabled). Every click resolves to the
   * customer's OWN school shop (services/shopDestination):
   *   1. "Studieturs favoritter"  — menu_1 products  → Studieturstrøjer shop / that product
   *   2. Vogntrøjer           — menu_2 cards     → Vogntrøjer shop / that item
   *   3. Pakkeløsninger       — menu_1 packages  → Studieturstrøjer shop / that package
   * Shop doesn't sell the item → the shop page; school without that shop type →
   * any open shop of the school; no open shop → the closed page; not
   * identified → the school popup (shop preselected), then the same rules. */
  const { menuItems, showSchoolSelector, openShopEnabled, landingProductsLimit } = useBrandSettings('clsdk');
  // anonToProductPage: without a school a card opens the product page (read-only), no popup.
  const onMenuItemClick = useMenuItemNavigation({ brandSlug: 'clsdk', feSlug: 'cls-dk', anonToProductPage: true });
  const toShop = (menuKey) => () => onMenuItemClick(menuKey, { toSchoolShop: true });
  const menu1Slug = (menuItems || []).find((m) => m.key === 'menu_1')?.slug;
  const menu2Slug = (menuItems || []).find((m) => m.key === 'menu_2')?.slug;
  const menu1Listing = useMenuItemListing('clsdk', menu1Slug, { showcase: true });
  const menu2Listing = useMenuItemListing('clsdk', menu2Slug, { showcase: true });
  const cardOptions = { feSlug: 'cls-dk', navigate, showSchoolSelector, openShopEnabled, onMenuItemClick, toSchoolShop: true, packageDescription: 'Pakkeløsning' };
  const studieturCards = mapMenuItemListingCards(menu1Listing, { ...cardOptions, menuKey: 'menu_1', only: 'products' });
  const vognCards      = mapMenuItemListingCards(menu2Listing, { ...cardOptions, menuKey: 'menu_2', only: 'products' });
  // Packages belong to the Studieturstrøjer collection (menu_1). Legacy admin setups keep
  // them in the menu_2 collection ("DK Default Packages") — still shown, and still carried
  // to the Studieturstrøjer shop (packages are matched by id, whichever listing they came from).
  const menu1Packages  = mapMenuItemListingCards(menu1Listing, { ...cardOptions, menuKey: 'menu_1', only: 'packages' });
  const packageCards   = menu1Packages.length
    ? menu1Packages
    : mapMenuItemListingCards(menu2Listing, { ...cardOptions, menuKey: 'menu_1', only: 'packages' });

  /* A visitor who has chosen a school sees THEIR shop's prices on every card — class and
   * shop overrides included — and only what that shop sells (services/shopCardPrices);
   * a grid with nothing left is not rendered. Anonymous visitors: the cards as they are.
   * While the shop's prices load the cards show without a price (no default-price flash). */
  const shopPrices = useShopCardPrices('clsdk');
  // Each block shows at most the brand's configured number of cards (Brands form →
  // "Front page: products per block") — cut AFTER the shop filter, so cards the
  // visitor's shop does not sell never take a slot.
  const forVisitor = (cards) => {
    const limited = (list) => list.slice(0, landingProductsLimit);
    if (!shopPrices.identified) return limited(cards);
    if (shopPrices.loading) return limited(cards).map((c) => ({ ...c, price: undefined }));
    return limited(applyShopPrices(cards, shopPrices, { feSlug: 'cls-dk' }));
  };
  const shown = {
    studieturCards: forVisitor(
      (studieturCards.length ? studieturCards : null)
            ?? mapProductCarouselItems(collectionWidget, { navigate, productBasePath: '/clothing/cls-dk/product-details', packageBasePath: '/clothing/cls-dk/product-package', schoolStorageKey: 'clsdk_school_selection', menuKey: 'menu_1', onMenuItemClick, toSchoolShop: true })
            ?? [
              { id: 1, image: require('../../../../assets/cls-dk/products/product-1-1.jpg'), imageAlt: 'STANDARD HOODIE', secondImage: require('../../../../assets/cls-dk/products/product-1-2.jpg'), secondImageAlt: 'STANDARD HOODIE Back', title: 'STANDARD HOODIE', description: 'Fruit of the Loom', price: '189,00DKK', colors: ['#363344', '#2e2421', '#a7bfd7', '#c9dbc8', '#b4a8d0', '#f470a4', '#337eb4', '#a2a2a2', '#000000', '#fec642', '#f6f6f8', '#415247', '#713c53', '#dddacc', '#555450', '#696b5b', '#f6b7d9'], onClick: () => onMenuItemClick('menu_1', { toSchoolShop: true, target: { type: 'product', productId: 43, name: 'STANDARD - HOODIE - FRUIT OF THE LOOM' } }) },
              { id: 2, image: require('../../../../assets/cls-dk/products/product-2-1.jpg'), imageAlt: 'ROYAL HOODIE', secondImage: require('../../../../assets/cls-dk/products/product-2-2.jpg'), secondImageAlt: 'ROYAL HOODIE Back', title: 'ROYAL HOODIE', price: '289,00DKK', colors: ['#552227', '#a2a2a2', '#1d1919', '#014337', '#e7e7e7', '#363344', '#c5a4af', '#a3180e', '#b8afa0', '#bed2d1'], onClick: () => onMenuItemClick('menu_1', { toSchoolShop: true, target: { type: 'product', productId: 44, name: 'ROYAL HOODIE' } }) },
              { id: 3, image: require('../../../../assets/cls-dk/products/product-3-1.jpg'), imageAlt: 'ROYAL CREWNECK', secondImage: require('../../../../assets/cls-dk/products/product-3-2.jpg'), secondImageAlt: 'ROYAL CREWNECK Back', title: 'ROYAL CREWNECK', price: '219,00DKK', colors: ['#363344', '#a2a2a2', '#1d1919', '#1f3746', '#b8afa0'], onClick: () => onMenuItemClick('menu_1', { toSchoolShop: true, target: { type: 'product', productId: 45, name: 'ROYAL CREWNECK' } }) },
              { id: 4, image: require('../../../../assets/cls-dk/products/product-4-1.jpg'), imageAlt: 'ROYAL ZIP HOODIE', secondImage: require('../../../../assets/cls-dk/products/product-4-2.jpg'), secondImageAlt: 'ROYAL ZIP HOODIE Back', title: 'ROYAL ZIP HOODIE', price: '289,00DKK', colors: ['#363344', '#1d1919', '#a2a2a2'], onClick: () => onMenuItemClick('menu_1', { toSchoolShop: true, target: { type: 'product', productId: 46, name: 'ROYAL ZIP HOODIE' } }) },
              { id: 5, image: require('../../../../assets/cls-dk/products/product-5-1.jpg'), imageAlt: 'OVERSIZED BOX FIT SWEATSHIRT', secondImage: require('../../../../assets/cls-dk/products/product-5-2.jpg'), secondImageAlt: 'OVERSIZED BOX FIT SWEATSHIRT Back', title: 'OVERSIZED BOX FIT SWEATSHIRT', price: '349,00DKK', colors: ['#e7e7e7', '#1d1919', '#f6f6f8', '#363344', '#a2a2a2', '#dddacc', '#c5a4af', '#b4a8d0', '#a7bfd7', '#c9dbc8', '#337eb4', '#415247', '#2e2421', '#b8afa0', '#696b5b', '#555450'], onClick: () => onMenuItemClick('menu_1', { toSchoolShop: true, target: { type: 'product', productId: 85, name: 'OVERSIZED BOX FIT SWEATSHIRT' } }) },
              { id: 8, image: require('../../../../assets/cls-dk/products/product-6-1.jpg'), imageAlt: 'OVERSIZED BOX FIT HOODIE', secondImage: require('../../../../assets/cls-dk/products/product-6-2.jpg'), secondImageAlt: 'OVERSIZED BOX FIT HOODIE Back', title: 'OVERSIZED BOX FIT HOODIE', price: '349,00DKK', colors: ['#a2a2a2', '#1d1919', '#f6f6f8', '#363344', '#dddacc', '#c5a4af', '#b4a8d0', '#1f3746', '#e7e7e7', '#337eb4', '#415247', '#2e2421', '#b8afa0', '#c9dbc8', '#f5ccd1', '#555450'], onClick: () => onMenuItemClick('menu_1', { toSchoolShop: true, target: { type: 'product', productId: 86, name: 'OVERSIZED BOX FIT HOODIE' } }) },
              { id: 9, image: require('../../../../assets/cls-dk/products/product-7-1.jpg'), imageAlt: 'OVERSIZED BOX FIT T-SHIRT', secondImage: require('../../../../assets/cls-dk/products/product-7-2.jpg'), secondImageAlt: 'OVERSIZED BOX FIT T-SHIRT Back', title: 'OVERSIZED BOX FIT T-SHIRT', price: '249,00DKK', colors: ['#1d1919', '#f6f6f8', '#363344', '#2e2421', '#dddacc', '#b4a8d0', '#a2a2a2', '#014337', '#415247'], onClick: () => onMenuItemClick('menu_1', { toSchoolShop: true, target: { type: 'product', productId: 88, name: 'OVERSIZED BOX FIT T-SHIRT' } }) },
              { id: 10, image: require('../../../../assets/cls-dk/products/product-8-1.jpg'), imageAlt: 'SWEATPANTS PREMIUM OPEN LEG', secondImage: require('../../../../assets/cls-dk/products/product-8-2.jpg'), secondImageAlt: 'SWEATPANTS PREMIUM OPEN LEG Back', title: 'SWEATPANTS PREMIUM OPEN LEG', price: '249,00DKK', colors: ['#1d1919', '#363344', '#e7e7e7'], onClick: () => onMenuItemClick('menu_1', { toSchoolShop: true, target: { type: 'product', productId: 84, name: 'SWEATPANTS PREMIUM OPEN LEG' } }) },
            ]),
    vognCards: forVisitor(
      (vognCards.length ? vognCards : null)
            ?? [
              { id: 1, image: require('../../../../assets/cls-dk/products/vognturer-product-1-1.jpg'), imageAlt: 'VOGNTRØJE POLO', secondImage: require('../../../../assets/cls-dk/products/vognturer-product-1-2.jpg'), secondImageAlt: 'VOGNTRØJE POLO Back', title: 'VOGNTRØJE POLO', price: '249,00DKK', colors: ['#2a3f36', '#6b7280', '#1d1919', '#f6f6f8'], onClick: () => onMenuItemClick('menu_2', { toSchoolShop: true, target: { type: 'product', productId: 92, name: 'VOGNTRØJE POLO' } }) },
              { id: 3, image: require('../../../../assets/cls-dk/products/vognturer-product-3-1.jpg'), imageAlt: 'FØRERVEST', secondImage: require('../../../../assets/cls-dk/products/vognturer-product-3-2.jpg'), secondImageAlt: 'FØRERVEST Back', title: 'FØRERVEST', price: '199,00DKK', colors: ['#d9e825'], onClick: () => onMenuItemClick('menu_2', { toSchoolShop: true, target: { type: 'product', productId: 93, name: 'FØRERVEST' } }) },
              { id: 2, image: require('../../../../assets/cls-dk/products/vognturer-product-2-1.jpg'), imageAlt: 'VOGNTRØJE', secondImage: require('../../../../assets/cls-dk/products/vognturer-product-2-2.jpg'), secondImageAlt: 'VOGNTRØJE Back', title: 'VOGNTRØJE', price: '149,00DKK', colors: ['#e83f97', '#5c1a22', '#f5c6d6', '#1f35c4', '#1d1919', '#f6f6f8', '#101a4f', '#3fc23f', '#c6e02a', '#f08a1c', '#8ec9e8', '#d6262a'], onClick: () => onMenuItemClick('menu_2', { toSchoolShop: true, target: { type: 'product', productId: 58, name: 'VOGNTRØJE' } }) }
            ]),
    packageCards: forVisitor(
      (packageCards.length ? packageCards : null)
            ?? mapProductCarouselItems(packagesWidget, { navigate, productBasePath: '/clothing/cls-dk/product-details', packageBasePath: '/clothing/cls-dk/product-package', schoolStorageKey: 'clsdk_school_selection', menuKey: 'menu_1', onMenuItemClick, toSchoolShop: true })
            ?? [
              { id: 13, image: require('../../../../assets/cls-dk/products/package-1-1.jpg'), imageAlt: 'STANDARD PAKKE 1', secondImage: require('../../../../assets/cls-dk/products/package-1-2.jpg'), secondImageAlt: 'STANDARD PAKKE 1 Back', title: 'STANDARD PAKKE 1', description: 'Fruit of the Loom', price: '399,00 KR',onClick: () => onMenuItemClick('menu_1', { toSchoolShop: true, target: { type: 'package', slug: 'standard-pakke-1-fruit-of-the-loom', name: 'STANDARD PAKKE 1 (FRUIT OF THE LOOM)' } }) },
              { id: 14, image: require('../../../../assets/cls-dk/products/package-2-1.jpg'), imageAlt: 'ROYAL PAKKE 2', secondImage: require('../../../../assets/cls-dk/products/package-2-2.jpg'), secondImageAlt: 'ROYAL PAKKE 2 Back', title: 'ROYAL PAKKE 2',  price: '389,00 KR',onClick: () => onMenuItemClick('menu_1', { toSchoolShop: true, target: { type: 'package', slug: 'royal-pakke-2', name: 'ROYAL PAKKE 2' } }) },
              { id: 15, image: require('../../../../assets/cls-dk/products/package-3-1.jpg'), imageAlt: 'ROYAL PAKKE 3', secondImage: require('../../../../assets/cls-dk/products/package-3-2.jpg'), secondImageAlt: 'ROYAL PAKKE 3 Back', title: 'ROYAL PAKKE 3',  price: '449,00 KR',onClick: () => onMenuItemClick('menu_1', { toSchoolShop: true, target: { type: 'package', slug: 'royal-pakke-3', name: 'ROYAL PAKKE 3' } }) },
            ]),
  };

  return (
    <div className="landing">
      

      {/* ── TopBar + Header ── */}
      <Header />

      {/* ── Hero Banner ── */}
      <HeroBannerV2
        subtitle="SKARPESTE PRISER MED PRISGARANTI"
        title="Design tøj til studietur og studenterkørsel"
        copy="En rejse, som skaber glæde og mindeværdige oplevelser."
        ctaLabel="Bestil studieturstrøjer"
        ctaOnClick={toShop('menu_1')}
        secondaryLabel="Bestil vogntrøje"
        secondaryOnClick={toShop('menu_2')}
        image={heroDesktop}
        imageMobile={heroMobile}
        imageAlt="Studietur og studenterkørsel"
      />

      {/* ── USP / Trustpilot Bar ── */}
      <USP
        width="layout"
        items={[
          { icon: 'fast-delivery', label: 'Gratis fragt' },
          { icon: 'size-guarantee', label: 'Personlig kontakt' },
          { icon: 'payment', label: 'Trykkeri i Danmark' },
        ]}
        trustpilot={{
          // TODO: replace with your actual Trustpilot Business Unit ID
          businessUnitId: '46905da900006400050004f8',
          locale: 'da-DK',
          reviewUrl: 'https://dk.trustpilot.com/review/cls.dk',
        }}
      />

      {/* ── Products Widget 1: Our Collection ── */}
      {shown.studieturCards.length > 0 && (
        <ProductsGrid
          moreLabel="flere"
          background="var(--color-gray-0)"
          title="Studieturs favoritter"
          subtitle="Alle produkter i vores kollektion er grundigt testet for at opfylde vores høje kvalitetskrav, vask efter vask."
          ctaLabel="Se alle kollektioner"
          ctaVariant="black"
          ctaIcon={<ArrowRight size={20} />}
          ctaOnClick={toShop('menu_1')}
          items={shown.studieturCards}
        />
      )}

      {/* ── Vognturer Banner ── */}
      <ContentBanner
        direction="left"
        align="center"
        subtitle="Klar til studenterkørslen"
        title="Bestil Vogntrøjer"
        titleVariant="h1"
        titleAs="h2"
        theme='dark'
        showBullets={false}
        showParagraph={false}
        contentBg='#34020C'
        slogan="Matchende trøjer til hele klassen, når I fejrer huen på vognen"
        linkLabel="Se vogntrøjer"
        onLinkClick={(e) => {
          e.preventDefault();
          onMenuItemClick('menu_2', { toSchoolShop: true });
        }}
        showCaption={false}
        image={require('../../../../assets/cls-dk/landing/vognturer.jpg')}
        imageAlt="Vogntrøjer til studenterkørsel"
      />

      {/* ── Products Widget 2: Vogntrøjer  ── */}
      {shown.vognCards.length > 0 && (
        <ProductsGrid
          moreLabel="flere"
          background="var(--color-gray-0)"
          desktopColumns={3}
          ctaIcon={<ArrowRight size={20} />}
          ctaOnClick={toShop('menu_2')}
          items={shown.vognCards}
        />
      )}

      {/* ── Fullwidth Content Widget 1 ── */}
      <Fullwidth
        showImage={false}
        subtitle="+ 75.000 KUNDER ÅRLIGT"
        mainCopyVariant="h2"
        mainCopy="Danmarks skarpeste priser med prisgaranti, hurtig levering og attraktive pakkeløsninger."
        additionalCopy="Bestil trygt og sikkert med over 100 års erfaring - og få en gratis trøjepakke til to repræsentanter."
        tags={['A4 rygtryk', '10 X 10 cm brystlogo', 'Gratis fragt', 'Nem individuel betaling', 'Frit valg af produkter']}
        showHyperlink={false}
      />

      {/* ── Secondary Banner (Package) ── */}
      <ContentBanner
        direction="right"
        align="center"
        subtitle="Rabatter op til 20%"
        title="Attraktive pakkeløsninger"
        titleVariant="h1"
        titleAs="h2"
        contentBg='#EBE6E0'
        showBullets={false}
        showParagraph={false}
        slogan="Spar op til 20% når du køber en pakkeløsning"
        linkLabel="Se pakkeløsninger"
        onLinkClick={(e) => {
          e.preventDefault();
          onMenuItemClick('menu_1', { toSchoolShop: true });
        }}
        showCaption={false}
        images={[
          { src: require('../../../../assets/cls-dk/landing/banner-1.jpg'), alt: 'Studieturpakken' },
          { src: require('../../../../assets/cls-dk/landing/banner-2.jpg'), alt: 'Hoodie & Sweatpants' },
          { src: require('../../../../assets/cls-dk/landing/banner-3.jpg'), alt: 'Hoodie & T-shirt' },
        ]}
        sliderInterval={5}
      />

      {/* ── Products Widget 3: Packages ── */}
      {shown.packageCards.length > 0 && (
        <ProductsGrid
          moreLabel="flere"
          background="var(--color-gray-0)"
          desktopColumns={3}
          ctaOnClick={toShop('menu_1')}
          items={shown.packageCards}
        />
      )}


      {/* ── Fullwidth Content Widget 2 (with image) ── */}
      <Fullwidth
        subtitle="BÆREDYGTIGHED OG FÆLLESSKAB"
        mainCopyVariant="h2"
        mainCopy="Hos C.L. SEIFERT ønsker vi at værne om miljøet"
        additionalCopy="Det er vigtigt for os, at produktionen er bæredygtig, og vi stræber efter at producere med op til 100% økologisk bomuld. Bæredygtighed er en kerneværdi i vores virksomhed, og vi arbejder konstant på at minimere vores miljøpåvirkning gennem ansvarlige produktionsmetoder. Studietursrejsen er en rejse, som ofte både forstærker personlige bånd og udvikler nye relationer i klassen og fællesskabet."
        image={require('../../../../assets/cls-dk/landing/about.jpg')}
        tags={['Trykkeri i Danmark', 'Overkommelige priser', 'Personlig kontakt']}
        showHyperlink={false}
        hyperlinkLabel="Læs mere"
        hyperlinkOnClick={(e) => {
          e.preventDefault();
          document.dispatchEvent(new CustomEvent('open-school-selector'));
        }}
      />

      {/* ── Footer ── */}
      <Footer />
    </div>
  );
};

export default Landing;
